import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useDeleteFeesAndExpense } from "../../../../../../api/services/client/clientApis";
import {
  ProgramExpenseData,
  ViewFeeOrExpenseDetails,
} from "../../../../../../api/services/program/models";
import {
  useGetCurrencies,
  useGetFeesOrExpenseById,
  useGetProgramFeesAndExpenses,
} from "../../../../../../api/services/program/programApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  WarningConfirmationModal,
} from "../../../../../../components";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import { MODULE_PERMISSIONS } from "../../../../../../helpers/constants";
import {
  TableActionType,
  TableActions,
} from "../../../../../../helpers/types/common";
import { getValueOrBlankArray } from "../../../../../../helpers/utils";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import { useGetProgramFeeConfig } from "../../../../hooks/useProgramFeeOrBillingConfig";
import { ProgramDetail } from "../../../../modelsAndSchemas/Models";
import { feesAndExpensesSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { AddFeesAndExpense } from "./components/addFeesAndExpense/AddFeesAndExpense";
import { FeesAndExpenseList } from "./components/FeesAndExpenseList";
import { FeesAndExpenseMainWrapper } from "./FeesAndExpenseMain.style";
interface FeesAndExpenseMainProps {
  isAddButtonVisible?: boolean;
  programId: string;
  poNumber?: string;
  billingCurrency?: string;
  isViewOnly?: boolean;
  isRefetch?: boolean;
  isEditProgram?: boolean;
  programDetail?: ProgramDetail;
}
export const FeesAndExpenseMain = ({
  isAddButtonVisible = true,
  billingCurrency: programFeeTabBillingCurrency,
  poNumber,
  programId,
  isViewOnly = false,
  isRefetch,
  isEditProgram,
  programDetail,
}: FeesAndExpenseMainProps) => {
  const { useFetchLabel, commonLabels } = useSiteCore();
  const getLabel = useFetchLabel(feesAndExpensesSiteCoreKeys.feesAndExpenses);
  const {
    programFeeBillingCurrency,
    isError: isGetProgramFeeConfigError,
    isLoading: isProgramFeeConfigLoading,
    fetchProgramFeeDetails,
  } = useGetProgramFeeConfig();
  const { data: expenseList, mutateAsync: getProgramFeesAndExpenses } =
    useGetProgramFeesAndExpenses();
  const { hasModulePermission } = usePermissions();

  const {
    mutateAsync: deleteFeesAndExpense,
    isPending: isDeleteFeesAndExpensePending,
    isError: isDeleteFeesAndExpenseError,
    isSuccess: isDeleteFeesAndExpenseSuccess,
  } = useDeleteFeesAndExpense();

  const [state, setState] = useState<{
    selectedExpenseDetails: ViewFeeOrExpenseDetails | ProgramExpenseData | null;
    showDeleteFeesAndExpenseModal: boolean;
    showAddFeesAndExpenseModal: boolean;
    feesExpenseList: ProgramExpenseData[];
  }>({
    showDeleteFeesAndExpenseModal: false,
    showAddFeesAndExpenseModal: false,
    selectedExpenseDetails: null,
    feesExpenseList: [],
  });

  const dispatch = useDispatch();

  const {
    data: expenseData,
    isPending: isGettingExpense,
    isSuccess: isGetExpenseSuccess,
    isError: isGetExpenseError,
    fetchById: getExpenseData,
  } = useGetFeesOrExpenseById();

  const { data: currencyList, isPending: isGettingCurrencyList } =
    useGetCurrencies();

  const billingCurrencyName = useMemo(() => {
    if (programFeeTabBillingCurrency) {
      return (
        currencyList?.find(
          (currency) => currency.id === programFeeTabBillingCurrency,
        )?.name ?? ""
      );
    } else if (programFeeBillingCurrency) {
      return programFeeBillingCurrency.name;
    }
    return "";
  }, [programFeeTabBillingCurrency, programFeeBillingCurrency, currencyList]);

  const handleAddFeesAndExpense = () => {
    setState((prevState) => ({
      ...prevState,
      showAddFeesAndExpenseModal: true,
      selectedExpenseDetails: null,
    }));
  };
  const billingCurrencyId = useMemo(() => {
    if (programFeeTabBillingCurrency) {
      return programFeeTabBillingCurrency;
    } else if (programFeeBillingCurrency) {
      return programFeeBillingCurrency.id;
    }
    return "";
  }, [programFeeTabBillingCurrency, programFeeBillingCurrency]);

  const deleteFeesAndExpenseConfirmationText = getLabel(
    feesAndExpensesSiteCoreKeys.deleteFeesAndExpenseContentText,
  );

  useEffect(() => {
    if (isRefetch) getProgramFeesAndExpenses(programId);
  }, [isRefetch, programId]);

  useEffect(() => {
    if (
      programId &&
      programDetail?.programTemplateId &&
      programDetail?.clientId &&
      !programFeeTabBillingCurrency
    ) {
      fetchProgramFeeDetails(
        programId,
        programDetail.clientId,
        programDetail.programTemplateId,
      );
    }
  }, [programId, programDetail, programFeeTabBillingCurrency]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      feesExpenseList: getValueOrBlankArray(expenseList),
    }));
  }, [expenseList]);
  useEffect(() => {
    if (
      isGettingExpense ||
      isGettingCurrencyList ||
      isProgramFeeConfigLoading ||
      isDeleteFeesAndExpensePending
    ) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isGettingExpense,
    isProgramFeeConfigLoading,
    isGettingCurrencyList,
    isDeleteFeesAndExpensePending,
  ]);

  useEffect(() => {
    if (
      isGetExpenseError ||
      isGetProgramFeeConfigError ||
      isDeleteFeesAndExpenseError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [
    isGetExpenseError,
    isGetProgramFeeConfigError,
    isDeleteFeesAndExpenseError,
  ]);

  useEffect(() => {
    if (isGetExpenseSuccess) {
      setState((prevState) => ({
        ...prevState,
        selectedExpenseDetails: expenseData,
        showAddFeesAndExpenseModal: true,
      }));
    }
  }, [isGetExpenseSuccess, expenseData]);

  useEffect(() => {
    if (isDeleteFeesAndExpenseSuccess) {
      const expenses = [...state.feesExpenseList];
      const index = expenses.findIndex(
        (expense) =>
          expense.id ===
          (state.selectedExpenseDetails as ProgramExpenseData).id,
      );
      if (index !== -1) {
        expenses.splice(index, 1);
      }
      setState((prevState) => ({
        ...prevState,
        showDeleteFeesAndExpenseModal: false,
        feesExpenseList: expenses,
      }));
    }
  }, [isDeleteFeesAndExpenseSuccess]);

  const handleActionClick = (
    selectedFeesAndExpense: ProgramExpenseData,
    actionName: TableActionType,
  ) => {
    if (actionName === TableActions.edit) {
      getExpenseData(selectedFeesAndExpense.id);
    } else if (actionName === TableActions.delete) {
      setState((prevState) => ({
        ...prevState,
        showDeleteFeesAndExpenseModal: true,
        selectedExpenseDetails: selectedFeesAndExpense,
      }));
    }
  };
  const onHideAddFeesAndExpenseModal = (isReloadList?: boolean) => {
    if (isReloadList) {
      getProgramFeesAndExpenses(programId);
    }
    setState((prevState) => ({
      ...prevState,
      showAddFeesAndExpenseModal: false,
    }));
  };
  const handleDeleteFeesAndExpenseModal = () => {
    setState((prevState) => ({
      ...prevState,
      showDeleteFeesAndExpenseModal: false,
      selectedExpenseDetails: null,
    }));
  };

  const handleConfirmFeesAndExpenseDelete = async () => {
    deleteFeesAndExpense(
      (state.selectedExpenseDetails as ProgramExpenseData).id,
    );
  };

  const hasAddFeesAndExpensePermission = hasModulePermission(
    MODULE_PERMISSIONS.ADD_FEE_AND_EXPENSES,
  );

  return (
    <FeesAndExpenseMainWrapper>
      <PermissionWrapper permission={MODULE_PERMISSIONS.VIEW_FEE_AND_EXPENSES}>
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={
            <AccordionHeaderText
              label={getLabel(feesAndExpensesSiteCoreKeys.billingItemsSection)}
            />
          }
        >
          <FeesAndExpenseList
            isAddButtonVisible={
              hasAddFeesAndExpensePermission ? isAddButtonVisible : false
            }
            handleAddFeesAndExpense={handleAddFeesAndExpense}
            onActionClick={handleActionClick}
            expenseList={state.feesExpenseList}
            programId={programId}
            billingCurrencyName={billingCurrencyName}
            onRefetchApi={getProgramFeesAndExpenses}
            isViewOnly={isViewOnly}
            poNumber={poNumber}
            isEditProgram={isEditProgram}
            programDetail={programDetail}
          />
        </AccordionHeader>
      </PermissionWrapper>
      {state.showAddFeesAndExpenseModal && (
        <AddFeesAndExpense
          onHideAddFeesAndExpenseModal={onHideAddFeesAndExpenseModal}
          selectedExpenseDetails={
            state.selectedExpenseDetails as ViewFeeOrExpenseDetails
          }
          showAddFeesAndExpenseModal={state.showAddFeesAndExpenseModal}
          currencyList={currencyList!}
          billingCurrencyId={billingCurrencyId}
          billingCurrencyName={billingCurrencyName}
          programId={programId}
        />
      )}
      {state.showDeleteFeesAndExpenseModal && (
        <WarningConfirmationModal
          labelContent={deleteFeesAndExpenseConfirmationText}
          modalTitle={commonLabels.confirmation}
          show={true}
          onClose={handleDeleteFeesAndExpenseModal}
          onPrimaryButtonClick={handleConfirmFeesAndExpenseDelete}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </FeesAndExpenseMainWrapper>
  );
};
